import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserRound } from 'lucide-react';
import { Button } from '../Button';
import { CompareIcon, ContactSalesIcon, DownloadIcon } from '../Button/icons';
import { useHeaderActions } from '../Layout/HeaderActionsContext';
import './GlobalHeader.css';

export interface GlobalHeaderProps {
  /** Optional className for styling customization */
  className?: string;
  /** Use regular anchor tags instead of React Router Links (useful for Storybook) */
  useAnchorTags?: boolean;
}

export const GlobalHeader = ({ className, useAnchorTags = false }: GlobalHeaderProps) => {
  const { headerActions } = useHeaderActions();
  const [isCompactActions, setIsCompactActions] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 1024 : true
  );
  const compareCount = headerActions.compareCount;
  const compareBadgeCount = compareCount > 99 ? '99+' : String(compareCount);
  const compareAriaLabel =
    compareCount > 0
      ? `Compare ${compareCount} selected ${compareCount === 1 ? 'product' : 'products'}`
      : 'Compare';

  useEffect(() => {
    const onResize = () => {
      setIsCompactActions(window.innerWidth < 1024);
    };

    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleLogoClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!headerActions.onLogoClick) return;
    event.preventDefault();
    headerActions.onLogoClick();
  };

  return (
    <header className={`global-header ${className || ''}`}>
      <div className="global-header-container">
        <div className="global-header-logo">
          {useAnchorTags ? (
            <a href="#" className="logo-link" onClick={handleLogoClick}>
              <img
                src="/av-logo.png"
                alt="AV Logo"
                className="logo-image"
              />
            </a>
          ) : (
            <Link to="/" className="logo-link" onClick={handleLogoClick}>
              <img
                src="/av-logo.png"
                alt="AV Logo"
                className="logo-image"
              />
            </Link>
          )}
        </div>

        <div className="global-header-actions" aria-label="Recommendation actions">
          <div className="global-header-action-wrap global-header-action-wrap--compare">
            <Button
              icon={<CompareIcon />}
              iconPosition="right"
              onClick={headerActions.onCompare}
              size="small"
              type="outline"
              isIconOnly={isCompactActions}
              aria-label={compareAriaLabel}
              className="global-header-action global-header-action--compare"
              disabled={compareCount === 0}
            >
              Compare
            </Button>
            {compareCount > 0 && (
              <span className="global-header-action-badge" aria-hidden="true">
                {compareBadgeCount}
              </span>
            )}
          </div>
          <Button
            icon={<DownloadIcon />}
            iconPosition="right"
            onClick={headerActions.onDownload}
            size="small"
            type="outline"
            isIconOnly={isCompactActions}
            aria-label="Download recommendation pack"
            className="global-header-action global-header-action--download"
          >
            Download Pack
          </Button>
          <Button
            icon={<ContactSalesIcon />}
            iconPosition="right"
            onClick={headerActions.onContactSales}
            size="small"
            type="primary"
            isIconOnly={false}
            aria-label="Talk to sales"
            className="global-header-action global-header-action--contact"
          >
            Talk to Sales
          </Button>
        </div>

        <button
          type="button"
          className="global-header-profile"
          aria-label="Signed in user"
          title="Signed in user"
        >
          <UserRound size={20} aria-hidden="true" />
        </button>
      </div>
      <div className="global-header-spacer" />
    </header>
  );
};
